import { useTranslation } from 'react-i18next'
import { Button, DataTable, Heading, Menu, MenuItem, Tile, TileGrid } from '../../ui'
import { Folder, FolderEditParams, FolderSearchResult } from '../../types'
import { useContext, useState } from 'react'
import { DEFAULT_ITEMS_PER_PAGE, SearchRecord, SearchTableProps } from './TableTypes'
import { ArchiveIcon, EditIcon, FolderIcon } from '../../ui/icons'
import { Pagination } from '../../ui/Pagination'
import { FolderEditModal } from './FolderEditModal'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import { Breadcrumbs } from '../../ui/Breadcrumbs'

export const FolderTable = <T extends SearchRecord>({ results, columnSort, params, setParams, reload, filters, ...rest }: Omit<SearchTableProps<T>, 'results'> & { results: FolderSearchResult<T> | null }) => {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [folder, setFolder] = useState<FolderEditParams | undefined>()

    const isSubFolder = !!results?.folder.parent_id
    const hasFolders = !!results?.folders.length
    const folderActions = ({ type, id }: Folder) => (
        <Button size="small" variant="secondary" onClick={() => {
            setFolder({ name: '', type, parent_id: id })
        }}>{t('folder_add')}</Button>
    )

    const handleFolderSave = async (folder: Folder) => {
        setFolder(undefined)
        handleChangeFolder(folder.id)
        if (params?.filter?.folder_id === folder.id) await reload?.()
    }

    const handleDeleteFolder = async (folder: Folder) => {
        if (!confirm(t('folder_delete_confirmation'))) return
        await api.folders.delete(project.id, folder.id)
        handleChangeFolder(folder.parent_id)
    }

    const handleChangeFolder = (folderId: number | undefined) => {
        setParams({
            ...params,
            filter: {
                ...params.filter,
                folder_id: folderId,
            },
            cursor: undefined,
            page: undefined,
        })
    }

    return <>
        {isSubFolder && (
            <Heading
                size="h3"
                title={results.folder.name}
                actions={<>
                    {folderActions(results.folder)}
                    <Menu size="small">
                        <MenuItem icon={<EditIcon />} onClick={() => setFolder(results.folder)}>
                            {t('edit')}
                        </MenuItem>
                        <MenuItem icon={<ArchiveIcon />} onClick={async () => await handleDeleteFolder(results.folder)}>
                            {t('delete')}
                        </MenuItem>
                    </Menu>
                </>}
            >
                <Breadcrumbs
                    items={results.folder.path}
                    onNavigate={id => handleChangeFolder(id)}
                />
            </Heading>
        )}
        {hasFolders && <>
            <Heading
                size="h4"
                title={t('folders')}
                actions={!isSubFolder ? folderActions(results.folder) : undefined}
            />
            <TileGrid numColumns={4}>
                {results?.folders.map(item =>
                    <Tile
                        title={item.name}
                        icon={<FolderIcon />}
                        key={item.id}
                        onClick={() => handleChangeFolder(item.id)}
                    />,
                )}
            </TileGrid>
        </>}
        <Heading
            size="h4"
            title={t('items')}
            actions={<>
                {filters}
                {!hasFolders && !isSubFolder && results?.folder ? folderActions(results.folder) : undefined}
            </>}
        />
        <DataTable {...rest}
            items={results?.results}
            isLoading={!results}
            columnSort={columnSort}
            onColumnSort={(onSort) => {
                const { sort, direction, ...prevParams } = params
                setParams({ ...prevParams, ...onSort })
            }}
        />
        {results && (
            <div>
                <Pagination
                    itemsPerPage={params.limit ?? DEFAULT_ITEMS_PER_PAGE}
                    onChangePage={page => setParams({ ...params, page })}
                    page={params.page ?? 1}
                    total={results.result_count}
                />
            </div>
        )}

        {folder && <FolderEditModal
            open={!!folder}
            onClose={() => setFolder(undefined)}
            onSave={handleFolderSave}
            folder={folder}
            path={results?.folder.path ?? []}
        />}
    </>
}
